import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { AppConfig } from "../AppConfig";
import { applyEdits, getMapView, isMainFeature } from "./arcgisUtils";
import { getAGEToken } from "./authenticate";
import { uuidv4 } from "./helpers";
import { toast } from "react-toastify";
import { getJobRelatedInvolvedObjects } from "./jobsUtils";
import { checkIfObjectExistsOnDefault } from "./versioningUtils";
import { NauticalObjectValidationResult } from "../types/types";
import { FeatureClasses, isOperativeLayerId } from "../constants/NauticalObjects";
import {
    filterDifferencesToObject,
    filterRelationshipRowsConnectedToObject,
    RelationshipFieldSpec,
} from "./jobForObjectUtils";
import {
    buildChangedKeySet,
    buildJobObjectKeySet,
    filterPlanFeatures,
    filterToJobConnectedFeatures,
} from "./planJobChangesUtils";
import type { VersionInfoExtended } from "@arcgis/core/versionManagement/support/jsonTypes";

export const getSessionID = () => {
    return uuidv4().toLowerCase();
};

export const getDifferencesObjectIds = async (
    versionGuid: string,
    sessionID: string,
    allowHjelpepunkt: boolean = false,
    throwOnError: boolean = false
): Promise<DifferencesObjectIDObject[]> => {
    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();
    let features: DifferencesObjectIDObject[] = [];
    const resultType = "objectIds";
    const urlSessionID = encodeURIComponent(sessionID);
    const urlWkid = encodeURIComponent("{wkid:32633}");
    await fetch(
        versionServiceUrl +
            `/versions/${versionGuid}/differences?f=json&sessionId=${urlSessionID}&resultType=${resultType}&outSR=${urlWkid}`,
        {
            method: "POST",
            headers: {
                "X-Esri-Authorization": "Bearer " + token,
            },
        }
    )
        .then(async (response) => {
            if (!response.ok && throwOnError) {
                throw new Error(`differences request failed: ${response.status}`);
            }
            await response.json().then((data) => {
                if (data && data.differences) {
                    features = data.differences;
                }
            });
        })
        .catch((error) => {
            console.error(error);
            if (throwOnError) throw error;
        });
    if (!allowHjelpepunkt) {
        // Remove layer 35 Hjelpepunkt these are excluded from jobs
        features = features.filter((f) => f.layerId !== 35);
    }
    return features;
};

export interface ReconcileResult {
    success: boolean;
    hasConflicts: boolean;
}

export const reconcileVersion = async (
    versionGuid: string,
    sessionID: string,
    abortIfConflict: boolean,
    conflicDetection: string,
    withPost: boolean
): Promise<ReconcileResult> => {
    const result: ReconcileResult = { success: false, hasConflicts: false };
    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();
    const urlSessionID = encodeURIComponent(sessionID);
    await fetch(
        versionServiceUrl +
            `/versions/${versionGuid}/reconcile?f=json&async=false&sessionId=${urlSessionID}&abortIfConflicts=${abortIfConflict}&conflictDetection=${conflicDetection}&withPost=${withPost}`,
        {
            method: "POST",
            headers: {
                "X-Esri-Authorization": "Bearer " + token,
            },
        }
    )
        .then(async (response) => {
            const data = await response.json();
            result.success = data?.success === true;
            result.hasConflicts = data?.hasConflicts === true;
            if (!result.success) {
                console.error("Failed to reconcile:", data?.error);
            }
        })
        .catch((error) => {
            console.error(error);
        });
    return result;
};

// Conflicts are never resolved from the app; every reconcile uses abortIfConflicts=true so
// that a human has to look at them outside the app before the version can be updated or posted.
export const RECONCILE_CONFLICT_MESSAGE =
    "Versjonen har konflikter med Planroot og kan ikke oppdateres eller sendes. Kontakt en administrator for å løse konfliktene.";

/**
 * Reconciles inside an already open edit session, aborting on conflicts. Shows a toast and
 * returns false if the reconcile failed or was aborted; the caller is responsible for closing the session.
 */
export const reconcileOrAbortOnConflicts = async (versionGuid: string, sessionID: string): Promise<boolean> => {
    const result = await reconcileVersion(versionGuid, sessionID, true, "byAttribute", false);
    if (result.hasConflicts) {
        toast.error(RECONCILE_CONFLICT_MESSAGE);
        return false;
    }
    if (!result.success) {
        toast.error("Klarte ikke å friske opp versjonen fra Planroot. Prøv igjen.");
        return false;
    }
    return true;
};

export type ReconcileFromDefaultStatus = "reconciled" | "conflicts" | "locked" | "failed";

/**
 * Pulls the latest changes from DEFAULT into the version in its own edit session, aborting
 * on conflicts. Returns "locked" when an edit session can't be opened, which typically
 * means another user is currently editing the version.
 */
export const reconcileFromDefault = async (versionGuid: string): Promise<ReconcileFromDefaultStatus> => {
    const sessionID = getSessionID();
    const cleanedVersionGuid = versionGuid.replace("{", "").replace("}", "");
    if (!(await startReading(cleanedVersionGuid, sessionID))) {
        return "failed";
    }
    if (!(await startEditing(cleanedVersionGuid, sessionID))) {
        await stopReading(cleanedVersionGuid, sessionID);
        return "locked";
    }
    const reconcileResult = await reconcileVersion(cleanedVersionGuid, sessionID, true, "byAttribute", false);
    // Reconcile commits automatically, so saveEdits=false does not discard it.
    await stopEditing(cleanedVersionGuid, sessionID, false);
    await stopReading(cleanedVersionGuid, sessionID);
    if (reconcileResult.hasConflicts) {
        return "conflicts";
    }
    return reconcileResult.success ? "reconciled" : "failed";
};

export const getConflicts = async (versionGuid: string, sessionID: string) => {
    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();
    const urlSessionID = encodeURIComponent(sessionID);
    await fetch(versionServiceUrl + `/versions/${versionGuid}/conflicts?f=json&sessionId=${urlSessionID}`, {
        method: "POST",
        headers: {
            "X-Esri-Authorization": "Bearer " + token,
        },
    }).catch((error) => {
        console.error(error);
    });
};

export const startReading = async (versionGuid: string, sessionID: string): Promise<boolean> => {
    let success = false;
    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();
    const urlSessionID = encodeURIComponent(sessionID);
    await fetch(versionServiceUrl + `/versions/${versionGuid}/startReading?f=json&sessionId=${urlSessionID}`, {
        method: "POST",
        headers: {
            "X-Esri-Authorization": "Bearer " + token,
        },
    })
        .then(async (response) => {
            await response.json().then((data) => {
                if (data.success) {
                    success = true;
                } else {
                    console.error("Failed to start reading:", data.error);
                }
            });
        })
        .catch((error) => {
            console.error(error);
        });
    return success;
};

export const stopReading = async (versionGuid: string, sessionID: string): Promise<boolean> => {
    let success = false;
    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();
    const urlSessionID = encodeURIComponent(sessionID);
    await fetch(versionServiceUrl + `/versions/${versionGuid}/stopReading?f=json&sessionId=${urlSessionID}`, {
        method: "POST",
        headers: {
            "X-Esri-Authorization": "Bearer " + token,
        },
    })
        .then(async (response) => {
            await response.json().then((data) => {
                if (data.success) {
                    success = true;
                } else {
                    console.error("Failed to stop reading:", data.error);
                }
            });
        })
        .catch((error) => {
            console.error(error);
        });
    return success;
};

export const startEditing = async (versionGuid: string, sessionID: string): Promise<boolean> => {
    let success = false;
    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();
    const urlSessionID = encodeURIComponent(sessionID);
    await fetch(versionServiceUrl + `/versions/${versionGuid}/startEditing?f=json&sessionId=${urlSessionID}`, {
        method: "POST",
        headers: {
            "X-Esri-Authorization": "Bearer " + token,
        },
    })
        .then(async (response) => {
            await response.json().then((data) => {
                if (data.success) {
                    success = true;
                } else {
                    console.error("Failed to start editing:", data.error);
                }
            });
        })
        .catch((error) => {
            console.error(error);
        });
    return success;
};

export const stopEditing = async (versionGuid: string, sessionID: string, saveEdits: boolean): Promise<boolean> => {
    let success = false;
    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();
    const urlSessionID = encodeURIComponent(sessionID);
    await fetch(
        versionServiceUrl +
            `/versions/${versionGuid}/stopEditing?f=json&sessionId=${urlSessionID}&saveEdits=${saveEdits}`,
        {
            method: "POST",
            headers: {
                "X-Esri-Authorization": "Bearer " + token,
            },
        }
    )
        .then(async (response) => {
            await response.json().then((data) => {
                if (data.success) {
                    success = true;
                } else {
                    console.error("Failed to stop editing:", data.error);
                }
            });
        })
        .catch((error) => {
            console.error(error);
        });
    return success;
};

export const postChanges = async (
    versionGuid: string,
    sessionID: string,
    rowsParameter: { layerId: number; objectIds: number[] }[]
): Promise<boolean> => {
    let success = false;

    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();
    const urlSessionID = encodeURIComponent(sessionID);

    const urlEncodedRowsParameter = encodeURIComponent(JSON.stringify(rowsParameter));

    if (rowsParameter.length === 0) {
        alert("Ingen endringer å poste. Kontakt Sander om du ser denne meldingen");
        return false;
    }

    await fetch(
        versionServiceUrl +
            `/versions/${versionGuid}/post?f=json&sessionId=${urlSessionID}&rows=${urlEncodedRowsParameter}`,
        {
            method: "POST",
            headers: {
                "X-Esri-Authorization": "Bearer " + token,
            },
        }
    )
        .then(async (response) => {
            await response.json().then((data) => {
                if (data.success) {
                    success = true;
                    return true;
                } else {
                    console.error("Failed to post changes:", data.error);
                }
            });
        })
        .catch((error) => {
            console.error(error);
        });

    return success;
};

export const alterVersion = async (
    versionGuid: string,
    newOwnerName?: string,
    newVersionName?: string,
    newDescription?: string
): Promise<boolean> => {
    let success = false;
    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();

    let paramString = "";
    if (newOwnerName) {
        paramString += `&ownerName=${newOwnerName}`;
    }

    if (newVersionName) {
        paramString += `&versionName=${newVersionName}`;
    }

    if (newDescription) {
        paramString += `&description=${newDescription}`;
    }

    await fetch(versionServiceUrl + `/versions/${versionGuid}/alter?f=json${paramString}`, {
        method: "POST",
        headers: {
            "X-Esri-Authorization": "Bearer " + token,
        },
    })
        .then(async (response) => {
            await response.json().then((data) => {
                if (data.success) {
                    success = true;
                    return true;
                }
            });
        })
        .catch((error) => {
            console.error(error);
        });
    return success;
};

const getVersionServiceUrl = () => {
    return AppConfig.ArcGIS.BaseUrl + AppConfig.ArcGIS.VersionManagementService;
};

/**
 * Find and groups all features that has changes in a version
 * Used for finding edits when creating a new job
 * @param versionGuid
 * @param versionName
 * @returns
 */
export const findDifferences = async (versionGuid: string, versionName: string): Promise<FeatureWithChanges[]> => {
    const progressToastId = "findDifferences-progress";

    // Initialize progress toast
    toast.info("Henter endringer fra versjon...", {
        toastId: progressToastId,
        progress: 0.1,
        autoClose: false,
    });

    const sessionID = uuidv4();
    const editedFeatures = await getDifferencesObjectIds(versionGuid, sessionID);

    toast.update(progressToastId, {
        render: "Laster inn endrede objekter...",
        progress: 0.3,
    });
    const jsonObjects = await getObjects(editedFeatures, versionName);

    toast.update(progressToastId, {
        render: "Finner relaterte objekter...",
        progress: 0.5,
    });
    const featuresWithChanges = await getAllRelatedFeaturesRecursive(
        jsonObjects,
        versionName,
        true,
        true,
        progressToastId
    );

    toast.update(progressToastId, {
        render: `Fant ${featuresWithChanges.length} hovedobjekter med tilhørende relaterte objekter`,
        type: "success",
        progress: 1,
        autoClose: 5000,
    });

    return featuresWithChanges;
};

/**
 * Like findDifferences, but scoped to a single, already-known main feature.
 *
 * Used by the "Opprett jobb på objektet" flow, where the target object is known
 * up front. This avoids loading and validating every active change in the
 * version: only the differences list (one cheap request) plus the version's
 * changed relationship-table rows are fetched, while object loading and
 * related-feature traversal run for just this object's combined object.
 *
 * Many:many relationship rows (e.g. layer-31 Lys_IB) are never returned by
 * queryRelatedFeatures, so — exactly like findDifferencesForPlan does for a whole
 * job — the version's changed relationship rows are loaded and fed into the relate
 * step; otherwise the object's combined-object link is lost and the connecting row
 * ends up unattached. The rows are scoped by foreign key to just the junctions that
 * reference this object, so unrelated combined objects are not pulled in.
 *
 * Assumes layerID/objectId refer to a MAIN feature — the caller
 * (createJobForObject) resolves child -> main via getMainRelatedFeature.
 *
 * Returns an array (length 0 or 1). The combined-object main-feature promotion
 * inside getAllRelatedFeaturesRecursive still applies, so the returned
 * mainFeature may differ from the requested object (e.g. a layer-0 light), which
 * matches the behaviour of the full findDifferences flow.
 */
export const findDifferencesForObject = async (
    versionGuid: string,
    versionName: string,
    layerID: number,
    objectId: string | number
): Promise<FeatureWithChanges[]> => {
    const sessionID = uuidv4();
    const allDifferences = await getDifferencesObjectIds(versionGuid, sessionID);

    const singleDiff = filterDifferencesToObject(allDifferences, layerID, objectId);
    if (!singleDiff) {
        return [];
    }

    // Load the requested object together with the version's changed many:many
    // relationship rows (the connecting junctions queryRelatedFeatures never returns).
    const relationshipLayers = AppConfig.Layers.filter((l) => l.RelationshipDetails);
    const relationshipDiffs = allDifferences.filter((d) => relationshipLayers.some((rl) => rl.LayerId === d.layerId));

    const loaded = await getObjects([singleDiff, ...relationshipDiffs], versionName);

    const objectFeatures = loaded.filter((f) => f.layerId === singleDiff.layerId);
    const requestedGlobalId = objectFeatures[0]?.versionGraphic?.attributes.globalid;

    const relationshipFieldsByLayer = new Map<number, RelationshipFieldSpec>(
        relationshipLayers.map((rl) => [
            rl.LayerId,
            {
                layerId: rl.LayerId,
                relation1FieldName: rl.RelationshipDetails!.relation1FieldName,
                relation2FieldName: rl.RelationshipDetails!.relation2FieldName,
            },
        ])
    );

    const relationshipRows = loaded.filter((f) => relationshipFieldsByLayer.has(f.layerId));
    const connectedRows = filterRelationshipRowsConnectedToObject(
        relationshipRows,
        requestedGlobalId,
        relationshipFieldsByLayer
    );

    const allRelevant = [...objectFeatures, ...connectedRows];
    const featuresWithChanges = await getAllRelatedFeaturesRecursive(allRelevant, versionName, true, true);

    return featuresWithChanges;
};

const getDifferencesObjectsFromJob = async (
    job: Graphic,
    versionName: string
): Promise<DifferencesObjectIDObject[]> => {
    const jobId = job.attributes.objectid;
    const jobFeatures = await getJobRelatedInvolvedObjects(jobId);

    const differencesObjects: DifferencesObjectIDObject[] = [];

    const featuresPerLayer: { [layerId: number]: Graphic[] } = {};

    const allLayers = getAllNFSLayers();
    if (!allLayers.length) {
        console.error("No layers found in the map.");
        return differencesObjects;
    }

    for (const feature of jobFeatures) {
        const layerId = feature.attributes.nautisk_objekt_kilde;
        if (layerId === undefined || layerId === null) {
            continue;
        }
        if (!featuresPerLayer[layerId]) {
            featuresPerLayer[layerId] = [];
        }
        featuresPerLayer[layerId].push(feature);
    }

    const promises: Promise<any>[] = [];
    const actualFeaturesPerLayer: { [layerId: number]: Graphic[] } = {};
    for (const layerId in featuresPerLayer) {
        const featureLayer = allLayers.find(
            (l) => (l as FeatureLayer).layerId === Number.parseInt(layerId)
        ) as FeatureLayer;

        const featureGlobalIds = featuresPerLayer[layerId].map((f) => f.attributes.nautisk_objekt_globalid);

        promises.push(
            featureLayer
                .queryFeatures({
                    outFields: ["objectid"],
                    where: `globalid IN ('${featureGlobalIds.join("','")}')`,
                    gdbVersion: versionName,
                })
                .then((result) => {
                    actualFeaturesPerLayer[layerId] = result.features;
                })
                .catch((error) => {
                    console.error(error);
                })
        );
    }

    await Promise.all(promises);

    for (const layerId in actualFeaturesPerLayer) {
        const updates: string[] = [];
        const features = actualFeaturesPerLayer[layerId];
        for (const feature of features) {
            updates.push(feature.attributes[(feature.layer as FeatureLayer).objectIdField]);
        }
        differencesObjects.push({
            layerId: Number(layerId),
            inserts: [],
            updates: updates,
            deletes: [],
        });
    }
    return differencesObjects;
};

/**
 * Find and groups all features that are added to a job via the involverte nautiske objekter relation
 * @param job
 * @param versionName
 * @returns
 */
export const findFeaturesWithChangesFromJob = async (
    job: Graphic,
    versionName: string
): Promise<FeatureWithChanges[]> => {
    const differencesObjectsFromJob = await getDifferencesObjectsFromJob(job, versionName);

    const featureObjects = await getObjects(differencesObjectsFromJob, versionName);

    const tempFeaturesWithChanges = await getAllRelatedFeaturesRecursive(featureObjects, versionName, true, false);
    const tempFeaturesWithChanges2 = await findAndAddMissingGraphics(tempFeaturesWithChanges, versionName);
    const featuresWithChanges = compareAndListChangedFields(tempFeaturesWithChanges2);
    return featuresWithChanges;
};

/**
 * Gathers the candidate features for the plan views: the job's linked objects plus the
 * version's deleted features and many:many relationship rows (neither of which is returned
 * by queryRelatedFeatures), all connected through the recursive relate step.
 *
 * Returns the connected candidates together with the version's changed-key set and the
 * many:many junction layer ids, so callers can apply whichever filter they need
 * (findDifferencesForPlan keeps only changed objects; findObjectsConnectedToPlan keeps all
 * job-connected objects).
 */
const gatherPlanCandidates = async (
    job: Graphic,
    versionGuid: string,
    versionName: string
): Promise<{
    allRelevantFeaturesWithChanges: FeatureWithChanges[];
    changedKeys: Set<string>;
    relationshipRowLayerIds: Set<number>;
    jobObjectKeys: Set<string>;
}> => {
    const sessionID = uuidv4();
    const allEditedFeatureIdsInVersion = await getDifferencesObjectIds(versionGuid, sessionID);

    // Grabbing just the deleted features from all diffrences since these are not found when querying related features.
    let deletedFeatureIdsInVersion = allEditedFeatureIdsInVersion.filter((f) => f.deletes && f.deletes.length > 0);
    deletedFeatureIdsInVersion = deletedFeatureIdsInVersion.map((f) => {
        return { deletes: f.deletes, layerId: f.layerId, inserts: [], updates: [] };
    });
    console.log("deleted features:", deletedFeatureIdsInVersion);
    const deletedFeaturesObjects = await getObjects(deletedFeatureIdsInVersion, versionName);
    console.log(deletedFeaturesObjects);

    // Grabbing the relationship features for many:many relationships since these are not returned when querying related features
    const relationshipTables = AppConfig.Layers.filter((l) => l.RelationshipDetails);
    const relationshipFeatureIdsInVersion = allEditedFeatureIdsInVersion.filter((f) =>
        relationshipTables.some((rt) => rt.LayerId === f.layerId)
    );
    console.log("relationship features:", relationshipFeatureIdsInVersion);
    const relationshipFeaturesObjects = await getObjects(relationshipFeatureIdsInVersion, versionName);

    const mainFeatureIdsInJob = await getDifferencesObjectsFromJob(job, versionName);
    const linkedTojobFeatureObjects = await getObjects(mainFeatureIdsInJob, versionName);

    // Joining
    // - the linked features
    // - the deleted features
    //      - This is to make sure we also get related features for deleted features since these are not found when just querying related features for the main features from the job.
    // - the relationship features
    //      - for many:many relationships, the relationship feature exists in its own table and is never returned when querying related features from the main feature
    const allRelevantFeatures = [
        ...linkedTojobFeatureObjects,
        ...deletedFeaturesObjects,
        ...relationshipFeaturesObjects,
    ];

    const allRelevantFeaturesWithChanges = await getAllRelatedFeaturesRecursive(
        allRelevantFeatures,
        versionName,
        true,
        true
    );
    console.log("allRelevantFeaturesWithChanges", allRelevantFeaturesWithChanges);

    const changedKeys = buildChangedKeySet(allEditedFeatureIdsInVersion);
    const relationshipRowLayerIds = new Set(relationshipTables.map((rt) => rt.LayerId));
    const jobObjectKeys = buildJobObjectKeySet(linkedTojobFeatureObjects);

    return { allRelevantFeaturesWithChanges, changedKeys, relationshipRowLayerIds, jobObjectKeys };
};

/**
 * Computes default/operative graphics and changed-field lists for a candidate set.
 *
 * Related features discovered recursively carry only a versionGraphic — they never pass
 * through getObjects, so their default/operative graphics and changed-field lists are unset.
 * Fetch the missing graphics, then compute changed fields (which includes the
 * "no default + no operative -> newly created, all fields changed" fallback). Without it,
 * related objects — new or edited — render as "Ingen endringer funnet".
 */
const resolveChangedFields = async (
    features: FeatureWithChanges[],
    versionName: string
): Promise<FeatureWithChanges[]> => {
    const withGraphics = await findAndAddMissingGraphics(features, versionName);
    return compareAndListChangedFields(withGraphics);
};

/**
 * Finding differences for plan before sending to QA1
 * @param versionGuid
 * @param versionName
 * @returns
 */
export const findDifferencesForPlan = async (
    job: Graphic,
    versionGuid: string,
    versionName: string
): Promise<FeatureWithChanges[]> => {
    const progressToastId = "findDifferences-progress-plan";

    // Initialize progress toast
    toast.info("Henter endringer fra versjon...", {
        toastId: progressToastId,
        progress: 0.1,
        autoClose: false,
    });

    const { allRelevantFeaturesWithChanges, changedKeys, relationshipRowLayerIds, jobObjectKeys } =
        await gatherPlanCandidates(job, versionGuid, versionName);

    toast.update(progressToastId, {
        render: "Finner endringer for objektene...",
        progress: 0.6,
    });

    // Determine which candidate features actually changed using the cheap diff id
    // list already fetched above, instead of re-loading and recursively traversing
    // every edit in the version. Candidates are first scoped to this job's objects:
    // the version can hold other jobs' edits, and their deletes would otherwise
    // survive as dangling standalone entries (see filterPlanFeatures).
    const filteredFeatures = filterPlanFeatures(
        allRelevantFeaturesWithChanges,
        changedKeys,
        relationshipRowLayerIds,
        jobObjectKeys
    );

    const featuresWithChangedFields = await resolveChangedFields(filteredFeatures, versionName);

    toast.update(progressToastId, {
        render: `Fant ${featuresWithChangedFields.length} hovedobjekter med tilhørende relaterte objekter`,
        type: "success",
        progress: 1,
        autoClose: 5000,
    });
    console.log("filteredFeatures", filteredFeatures);

    return featuresWithChangedFields;
};

/**
 * Attaches unchanged many:many junction rows (e.g. Lys_IB) from DEFAULT to the connected
 * feature groups.
 *
 * queryRelatedFeatures never returns junction rows, and gatherPlanCandidates only loads the
 * ones present in the version diff — so a combined object with no pending changes shows the
 * light and IB but not the (unchanged) junction linking them. This queries each junction
 * table on DEFAULT for rows whose foreign keys reference the already-connected objects and
 * attaches any not already present, so the full combined object is displayed. Rows that did
 * change are loaded from the version diff and skipped here (no duplication). Mutates the
 * passed groups in place.
 */
const addUnchangedRelationshipRowsFromDefault = async (features: FeatureWithChanges[]): Promise<void> => {
    const relationshipLayers = AppConfig.Layers.filter((l) => l.RelationshipDetails);
    if (!relationshipLayers.length) return;

    const allLayers = getAllNFSLayers();
    if (!allLayers.length) return;

    const relationshipLayerIds = new Set(relationshipLayers.map((l) => l.LayerId));

    // Map each connected non-junction object's globalid to the group it belongs to, so a
    // found junction row can be attached to the right combined object. Also record the
    // junction rows already present (loaded from the version diff) to avoid duplicates.
    const globalIdToGroup = new Map<string, FeatureWithChanges>();
    const existingRowKeys = new Set<string>();
    for (const group of features) {
        const collect = (f: FeatureObject) => {
            if (relationshipLayerIds.has(f.layerId)) {
                existingRowKeys.add(`${f.layerId}:${f.objectId}`);
                return;
            }
            const gid = f.versionGraphic?.attributes.globalid ?? f.defaultGraphic?.attributes.globalid;
            if (gid) globalIdToGroup.set(gid, group);
        };
        collect(group.mainFeature);
        group.relatedFeatures.forEach(collect);
    }

    if (globalIdToGroup.size === 0) return;
    const inList = [...globalIdToGroup.keys()].map((g) => `'${g}'`).join(",");

    // Each relationship layer is queried independently; run them concurrently. The result
    // handling stays race-free: each layer only adds keys prefixed with its own LayerId, and
    // the synchronous block after each await completes without interleaving.
    await Promise.all(
        relationshipLayers.map(async (relLayer) => {
            const spec = relLayer.RelationshipDetails!;
            const featureLayer = allLayers.find(
                (l) => (l as FeatureLayer).layerId === relLayer.LayerId
            ) as FeatureLayer;
            if (!featureLayer) return;

            try {
                const result = await featureLayer.queryFeatures({
                    where: `${spec.relation1FieldName} IN (${inList}) OR ${spec.relation2FieldName} IN (${inList})`,
                    outFields: ["*"],
                    returnGeometry: false,
                    gdbVersion: "sde.DEFAULT",
                });
                for (const rowGraphic of result.features) {
                    rowGraphic.layer ??= featureLayer;
                    const objectId = String(rowGraphic.attributes[featureLayer.objectIdField]);
                    const rowKey = `${relLayer.LayerId}:${objectId}`;
                    if (existingRowKeys.has(rowKey)) continue;
                    existingRowKeys.add(rowKey);

                    const group =
                        globalIdToGroup.get(rowGraphic.attributes[spec.relation1FieldName]) ??
                        globalIdToGroup.get(rowGraphic.attributes[spec.relation2FieldName]);
                    if (!group) continue;

                    // Unchanged row (version === default), so no changed fields to compute.
                    group.relatedFeatures.push({
                        type: "update",
                        layerId: relLayer.LayerId,
                        objectId,
                        versionGraphic: rowGraphic,
                        defaultGraphic: rowGraphic,
                        changedFieldsDefault: [],
                        changedFieldsOperative: [],
                    });
                }
            } catch (error) {
                console.error(`Error querying relationship layer ${relLayer.LayerId} on DEFAULT:`, error);
            }
        })
    );
};

/**
 * Like findDifferencesForPlan, but keeps every object connected to the job even when it has
 * no pending version-vs-DEFAULT difference.
 *
 * Used by the "plan rejected" state: the job's changes have already been posted to DEFAULT
 * (so the version diff is empty), yet the user still needs to see the connected objects to
 * act on the QA rejection. When the user then edits an object (outside this panel) the new
 * version changes — including the many:many relationship rows and deletes gathered here —
 * are picked up so the resubmit to QA1 posts a complete combined object.
 *
 * A version can hold several jobs' changes, so the candidate set is scoped to this job's
 * objects (filterToJobConnectedFeatures): other jobs' deletes and many:many rows that the
 * version diff drags in are dropped, while this job's deletes (sector lines/areas) stay
 * attached to their light and are posted.
 *
 * Unchanged junction rows are then attached from DEFAULT so the full combined object shows.
 */
export const findObjectsConnectedToPlan = async (
    job: Graphic,
    versionGuid: string,
    versionName: string
): Promise<FeatureWithChanges[]> => {
    const progressToastId = "findConnectedObjects-progress-plan";

    toast.info("Henter objekter knyttet til jobben...", {
        toastId: progressToastId,
        progress: 0.1,
        autoClose: false,
    });

    const { allRelevantFeaturesWithChanges, jobObjectKeys } = await gatherPlanCandidates(
        job,
        versionGuid,
        versionName
    );

    toast.update(progressToastId, {
        render: "Finner endringer for objektene...",
        progress: 0.6,
    });

    const connectedFeatures = filterToJobConnectedFeatures(allRelevantFeaturesWithChanges, jobObjectKeys);

    const featuresWithChangedFields = await resolveChangedFields(connectedFeatures, versionName);

    // resolveChangedFields populates the objects' graphics, so their globalids are now
    // available to look up the unchanged junction rows that link them.
    await addUnchangedRelationshipRowsFromDefault(featuresWithChangedFields);

    toast.update(progressToastId, {
        render: `Fant ${featuresWithChangedFields.length} objekter knyttet til jobben`,
        type: "success",
        progress: 1,
        autoClose: 5000,
    });
    console.log("connectedFeatures", featuresWithChangedFields);

    return featuresWithChangedFields;
};

export interface DifferencesObjectIDObject {
    layerId: number;
    inserts: string[];
    updates: string[];
    deletes: string[];
}

export interface FeatureObject {
    type: "insert" | "update" | "delete";
    layerId: number;
    objectId: string;
    versionGraphic?: Graphic;
    operativeGraphic?: Graphic;
    defaultGraphic?: Graphic;
    changedFieldsDefault?: string[];
    changedFieldsOperative?: string[];
}

export interface FeatureWithChanges {
    mainFeature: FeatureObject;
    relatedFeatures: FeatureObject[];
    validationResult?: NauticalObjectValidationResult;
}

const getObjects = async (
    objectsToFetch: DifferencesObjectIDObject[],
    versionName: string
): Promise<FeatureObject[]> => {
    const featureArray: FeatureObject[] = [];
    const promises: Promise<any>[] = [];

    const allLayers = getAllNFSLayers();
    if (!allLayers.length) {
        console.error("No layers found in the map.");
        return featureArray;
    }

    for (const differences of objectsToFetch) {
        const layerId = differences.layerId;
        const featureLayer = allLayers.find((l) => (l as FeatureLayer).layerId === layerId) as FeatureLayer;

        // Match on layerId in addition to objectId: objectIds are only unique
        // within a layer, so a single-object load that mixes the main object's
        // layer with changed relationship-row layers can otherwise attach a
        // graphic to a colliding objectId on the wrong layer.
        const findEditedFeature = (feature: Graphic) =>
            featureArray.find(
                (f) => f.layerId === layerId && f.objectId === feature.attributes[featureLayer.objectIdField]
            );

        const updatedAndDeletedFeatureIds: string[] = [];
        const insertedFeatureIds: string[] = [];
        if (differences.inserts) {
            for (const insert of differences.inserts) {
                insertedFeatureIds.push(insert);
                featureArray.push({
                    type: "insert",
                    layerId: layerId,
                    objectId: insert,
                });
            }
        }
        if (differences.updates) {
            for (const update of differences.updates) {
                updatedAndDeletedFeatureIds.push(update);
                featureArray.push({
                    type: "update",
                    layerId: layerId,
                    objectId: update,
                });
            }
        }
        if (differences.deletes) {
            for (const del of differences.deletes) {
                updatedAndDeletedFeatureIds.push(del);
                featureArray.push({
                    type: "delete",
                    layerId: layerId,
                    objectId: del,
                });
            }
        }

        if (updatedAndDeletedFeatureIds.length > 0) {
            promises.push(
                featureLayer
                    .queryFeatures({
                        objectIds: updatedAndDeletedFeatureIds,
                        outFields: ["*"],
                        returnGeometry: true,
                        geometryPrecision: 20,
                        gdbVersion: "sde.DEFAULT",
                    })
                    .then(async (result) => {
                        for (const feature of result.features) {
                            const edited = findEditedFeature(feature);

                            // Unsure if setting the layer from mapview here is correct since the layer is versioned
                            feature.layer ??= featureLayer;
                            if (edited) {
                                edited.defaultGraphic = feature;
                            }
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    })
            );

            promises.push(
                featureLayer
                    .queryFeatures({
                        objectIds: updatedAndDeletedFeatureIds,
                        outFields: ["*"],
                        returnGeometry: true,
                        geometryPrecision: 20,
                        gdbVersion: versionName,
                    })
                    .then((versionedResult) => {
                        for (const feature of versionedResult.features) {
                            const edited = findEditedFeature(feature);
                            feature.layer ??= featureLayer;
                            if (edited) {
                                edited.versionGraphic = feature;
                            }
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    })
            );
        }
        if (insertedFeatureIds.length > 0) {
            const insertedFeatures = await featureLayer.queryFeatures({
                objectIds: insertedFeatureIds,
                outFields: ["*"],
                returnGeometry: true,
                geometryPrecision: 20,
                gdbVersion: versionName,
            });

            for (const feature of insertedFeatures.features) {
                const edited = findEditedFeature(feature);
                feature.layer ??= featureLayer;
                if (edited) {
                    edited.versionGraphic = feature;
                }
            }
        }
    }

    await Promise.all(promises);

    const globalIdsPerLayerId: { [layerId: number]: string[] } = {};
    for (const feature of featureArray) {
        if (!globalIdsPerLayerId[feature.layerId]) {
            globalIdsPerLayerId[feature.layerId] = [];
        }
        if (feature.versionGraphic && feature.versionGraphic.attributes.globalid) {
            globalIdsPerLayerId[feature.layerId].push(feature.versionGraphic.attributes.globalid);
        }
    }

    const operativePromises: Promise<any>[] = [];
    for (const layerId in globalIdsPerLayerId) {
        // The operative feature server does not have all the layers of the editing
        // feature server, so skip the ones that would produce invalid api calls.
        if (!isOperativeLayerId(layerId)) {
            continue;
        }

        const operativeFeatureLayer = new FeatureLayer({
            // This is a critical "hack" to make sure position of features are correct
            portalItem: {
                id: AppConfig.OperativeObjectsPortalItemId,
                portal: {
                    url: AppConfig.ArcGIS.BaseUrl,
                },
            },
            url: `${AppConfig.OperativeObjectsFeatureServer}${layerId}`,
        });

        if (!operativeFeatureLayer.loaded) {
            try {
                await operativeFeatureLayer.load();
            } catch (error) {
                console.error(error);
                continue;
            }
        }

        operativePromises.push(
            operativeFeatureLayer
                .queryFeatures({
                    where: `globalid IN ('${globalIdsPerLayerId[layerId].join("','")}')`,
                    outFields: ["*"],
                    returnGeometry: true,
                    geometryPrecision: 20,
                })
                .then((versionedResult) => {
                    for (const feature of versionedResult.features) {
                        const edited = featureArray.find((f) => {
                            return f.versionGraphic?.attributes.globalid === feature.attributes.globalid;
                        });
                        if (edited) {
                            feature.layer ??= operativeFeatureLayer;
                            edited.operativeGraphic = feature;
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
        );
    }

    await Promise.all(operativePromises);

    for (const feature of featureArray) {
        if (feature.type === "delete") {
            continue;
        }

        if (feature.type === "insert") {
            const changedFieldsDefault: string[] = [];
            for (const key in feature.versionGraphic?.attributes) {
                changedFieldsDefault.push(key);
            }
            feature.changedFieldsDefault = changedFieldsDefault;
            continue;
        }

        const changedFieldsDefault: string[] = [];
        const changedFieldsOperative: string[] = [];
        for (const key in feature.defaultGraphic?.attributes) {
            if (feature.defaultGraphic?.attributes[key] !== feature.versionGraphic?.attributes[key]) {
                changedFieldsDefault.push(key);
            }
        }
        feature.changedFieldsDefault = changedFieldsDefault;

        for (const key in feature.operativeGraphic?.attributes) {
            if (feature.operativeGraphic?.attributes[key] !== feature.versionGraphic?.attributes[key]) {
                changedFieldsOperative.push(key);
            }
        }
        feature.changedFieldsOperative = changedFieldsOperative;

        // No default and no operative graphic -> the object only exists in the version,
        // i.e. it is newly created and not yet pushed to default/operative. The diff loops
        // above produce empty lists in that case, so treat every version field as changed.
        // (Job objects always arrive here typed as "update" via getDifferencesObjectsFromJob,
        // so they never hit the "insert" branch that would otherwise cover this.)
        if (!feature.defaultGraphic && !feature.operativeGraphic) {
            const allFields = Object.keys(feature.versionGraphic?.attributes ?? {});
            feature.changedFieldsDefault = allFields;
            feature.changedFieldsOperative = allFields;
        }
    }

    return featureArray;
};

export const getAllRelatedFeaturesRecursive = async (
    features: FeatureObject[],
    versionName: string,
    keepRelatedOutsideOfInitialFeatures: boolean,
    isFromVersionChanges: boolean,
    progressToastId?: string
): Promise<FeatureWithChanges[]> => {
    const featuresWithChanges: FeatureWithChanges[] = [];
    // Deleted features does not have versionGraphics
    const mainFeatures = features.filter((f) => {
        const featureGraphic = f.versionGraphic ?? f.defaultGraphic;
        if (!featureGraphic) {
            return false;
        }
        return isMainFeature(featureGraphic);
    });

    // Create a lookup map for all original features for quick validation
    const originalFeaturesMap = new Map<string, FeatureObject>();
    for (const feature of features) {
        const featureKey = `${feature.layerId}:${feature.objectId}`;
        originalFeaturesMap.set(featureKey, feature);
    }

    // Process each main feature individually to maintain proper mapping
    for (let i = 0; i < mainFeatures.length; i++) {
        const mainFeature = mainFeatures[i];

        if (progressToastId && mainFeatures.length > 1) {
            const progress = 0.5 + 0.4 * (i / mainFeatures.length);
            toast.update(progressToastId, {
                render: `Behandler hovedobjekt ${i + 1} av ${mainFeatures.length} (Layer ${mainFeature.layerId})...`,
                progress: progress,
            });
        }

        const relatedFeatures = await findAllRelatedFeaturesForMainFeature(
            mainFeature,
            originalFeaturesMap,
            versionName,
            keepRelatedOutsideOfInitialFeatures,
            isFromVersionChanges
        );
        featuresWithChanges.push({
            mainFeature: mainFeature,
            relatedFeatures: relatedFeatures,
        });
    }

    const listOfParentlessFeatures: FeatureObject[] = [];

    // if any non-main features was not added as related features, add them as main features with empty related features
    const allAddedRelatedFeatureObjectids = [];
    for (const featureWithChange of featuresWithChanges) {
        for (const relatedFeature of featureWithChange.relatedFeatures) {
            allAddedRelatedFeatureObjectids.push(relatedFeature.objectId);
        }
    }
    for (const feature of features) {
        if (isMainFeature(feature.versionGraphic!)) {
            continue;
        }
        if (!allAddedRelatedFeatureObjectids.includes(feature.objectId)) {
            listOfParentlessFeatures.push(feature);
        }
    }

    for (const parentlessFeature of listOfParentlessFeatures) {
        const featuresLayerId = parentlessFeature.layerId;
        const layerConfig = AppConfig.Layers.find((l) => l.LayerId === featuresLayerId);
        const relationshipDetails = layerConfig?.RelationshipDetails;

        if (!relationshipDetails) {
            continue;
        }

        const { relation1LayerId, relation2LayerId, relation1FieldName, relation2FieldName } = relationshipDetails;

        // Going through all main features and related features to find a match for both relations
        for (const featureWithChange of featuresWithChanges) {
            let matches1 = false;
            let matches2 = false;

            // Check main feature for relation 1
            if (featureWithChange.mainFeature.layerId === relation1LayerId) {
                if (
                    featureWithChange.mainFeature.versionGraphic?.attributes.globalid ===
                    parentlessFeature.versionGraphic?.attributes[relation1FieldName]
                ) {
                    matches1 = true;
                }
            }
            // Check main feature for relation 2
            if (featureWithChange.mainFeature.layerId === relation2LayerId) {
                if (
                    featureWithChange.mainFeature.versionGraphic?.attributes.globalid ===
                    parentlessFeature.versionGraphic?.attributes[relation2FieldName]
                ) {
                    matches2 = true;
                }
            }
            // Check related features for relation 1 and 2
            for (const relatedFeature of featureWithChange.relatedFeatures) {
                if (relatedFeature.layerId === relation1LayerId) {
                    if (
                        relatedFeature.versionGraphic?.attributes.globalid ===
                        parentlessFeature.versionGraphic?.attributes[relation1FieldName]
                    ) {
                        matches1 = true;
                    }
                }
                if (relatedFeature.layerId === relation2LayerId) {
                    if (
                        relatedFeature.versionGraphic?.attributes.globalid ===
                        parentlessFeature.versionGraphic?.attributes[relation2FieldName]
                    ) {
                        matches2 = true;
                    }
                }
            }
            if (matches1 && matches2) {
                featureWithChange.relatedFeatures.push(parentlessFeature);
            }
        }
    }

    // Some kind of logic to make sure we show the main feature of combined objects
    // If the related features contains a feature from layer 30, 31 or 32, and the main feature is from layer 0, then it is a combined object with light as main feature
    // That is correct
    // If The main feature is from layer 1, 4 or 6, and the related features contains a feature from layer 30, 31 or 32 and a feature from layer 0, then the main feature should be the one from layer 0

    const filteredFeaturesWithChanges: FeatureWithChanges[] = [];
    for (const featureWithChange of featuresWithChanges) {
        if (
            featureWithChange.mainFeature.layerId === 1 ||
            featureWithChange.mainFeature.layerId === 4 ||
            featureWithChange.mainFeature.layerId === 6
        ) {
            // Check if related features contains a feature from layer 0
            const hasRelatedFrom0 = featureWithChange.relatedFeatures.some((rf) => rf.layerId === 0);
            if (hasRelatedFrom0) {
                // Find the feature from layer 0
                const newMainFeature = featureWithChange.relatedFeatures.find((rf) => rf.layerId === 0);
                if (newMainFeature) {
                    // Remove the new main feature from related features
                    featureWithChange.relatedFeatures = featureWithChange.relatedFeatures.filter(
                        (rf) => rf.objectId !== newMainFeature.objectId
                    );
                    // Add the old main feature to related features
                    featureWithChange.relatedFeatures.push(featureWithChange.mainFeature);
                    // Set the new main feature
                    featureWithChange.mainFeature = newMainFeature;
                }
            }
        }
        filteredFeaturesWithChanges.push(featureWithChange);
    }

    // Remove duplicates incase correct mainfeature change already existed
    // Check that every mainfeature is unique
    const uniqueMainFeatureKeys = new Set<string>();
    const finalFilteredFeaturesWithChanges: FeatureWithChanges[] = [];
    for (const featureWithChange of filteredFeaturesWithChanges) {
        const featureKey = `${featureWithChange.mainFeature.layerId}:${featureWithChange.mainFeature.objectId}`;
        if (!uniqueMainFeatureKeys.has(featureKey)) {
            uniqueMainFeatureKeys.add(featureKey);
            finalFilteredFeaturesWithChanges.push(featureWithChange);
        }
    }

    // Need to make sure deleted non-main features are added to their main features
    // In theory only light sectors, lines and areas should be able to be deleted.
    // Possibly also relation table rows for layers 25-32 though this is not possible in current web app.
    // Will handle just in case.
    const sectorToLightMap: { [sectorGuid: string]: string } = {};
    const unmappedDeletedFeatures: FeatureObject[] = [];
    for (const feature of features) {
        const featureGraphic = feature.versionGraphic ?? feature.defaultGraphic;
        if (!featureGraphic) {
            continue;
        }
        if (isMainFeature(featureGraphic)) {
            continue;
        }

        if (feature.type !== "delete") {
            continue;
        }

        // Find the main feature for this deleted non-main feature
        switch (feature.layerId) {
            case 19: {
                // Light sector, related to main feature in layer 0 (Lys)
                const mainFeatureForDeleted = finalFilteredFeaturesWithChanges.find(
                    (fwc) =>
                        fwc.mainFeature.layerId === 0 &&
                        fwc.mainFeature.versionGraphic?.attributes.globalid === featureGraphic.attributes["fknavinst"]
                );
                if (mainFeatureForDeleted) {
                    mainFeatureForDeleted.relatedFeatures.push(feature);
                    // Store in map for later use when handling light lines and areas
                    sectorToLightMap[featureGraphic.attributes.globalid] =
                        mainFeatureForDeleted.mainFeature.versionGraphic?.attributes.globalid;
                }
                break;
            }
            case 25:
            case 26:
            case 27:
            case 28:
            case 29: {
                // These layers are related to main feature in layer 9 (Farled)
                const mainFeatureForDeleted = finalFilteredFeaturesWithChanges.find(
                    (fwc) =>
                        fwc.mainFeature.layerId === 9 &&
                        fwc.mainFeature.versionGraphic?.attributes.globalid === featureGraphic.attributes["farled_guid"]
                );
                if (mainFeatureForDeleted) {
                    mainFeatureForDeleted.relatedFeatures.push(feature);
                }
                break;
            }
            case 30:
            case 31:
            case 32: {
                // These layers are related to main feature in layer 0 (Lys)
                const mainFeatureForDeleted2 = finalFilteredFeaturesWithChanges.find(
                    (fwc) =>
                        fwc.mainFeature.layerId === 0 &&
                        fwc.mainFeature.versionGraphic?.attributes.globalid === featureGraphic.attributes["lys_guid"]
                );
                if (mainFeatureForDeleted2) {
                    mainFeatureForDeleted2.relatedFeatures.push(feature);
                }
                break;
            }
            default: {
                // Could not map deleted non-main feature to any main feature
                unmappedDeletedFeatures.push(feature);
                break;
            }
        }
    }

    for (const unmappedDeletedFeature of unmappedDeletedFeatures) {
        const featureGraphic = unmappedDeletedFeature.versionGraphic ?? unmappedDeletedFeature.defaultGraphic;
        if (!featureGraphic) {
            continue;
        }
        if (
            unmappedDeletedFeature.layerId === FeatureClasses.Sektorlinje ||
            unmappedDeletedFeature.layerId === FeatureClasses.Sektorflate ||
            unmappedDeletedFeature.layerId === FeatureClasses.Skjerming
        ) {
            // Sector line or area
            const fksectorId = featureGraphic.attributes["fksektorid"];
            const lightGuid = sectorToLightMap[fksectorId];
            if (lightGuid) {
                const mainFeatureForDeleted = finalFilteredFeaturesWithChanges.find(
                    (fwc) =>
                        fwc.mainFeature.layerId === 0 &&
                        fwc.mainFeature.versionGraphic?.attributes.globalid === lightGuid
                );
                if (mainFeatureForDeleted) {
                    mainFeatureForDeleted.relatedFeatures.push(unmappedDeletedFeature);
                }
            }
        }
    }

    // Fallback to make sure no changes are missed
    const allAddedRelatedFeatureObjectidsReduced = [];
    for (const featureWithChange of finalFilteredFeaturesWithChanges) {
        for (const relatedFeature of featureWithChange.relatedFeatures) {
            allAddedRelatedFeatureObjectidsReduced.push(relatedFeature.objectId);
        }
    }
    for (const feature of features) {
        if (isMainFeature(feature.versionGraphic!)) {
            continue;
        }
        if (!allAddedRelatedFeatureObjectidsReduced.includes(feature.objectId)) {
            finalFilteredFeaturesWithChanges.push({
                mainFeature: feature,
                relatedFeatures: [],
            });
        }
    }
    return finalFilteredFeaturesWithChanges;
};

const compareAndListChangedFields = (featuresWithChanges: FeatureWithChanges[]): FeatureWithChanges[] => {
    const clonedFeaturesWithChanges = [...featuresWithChanges];
    for (const featureWithChange of clonedFeaturesWithChanges) {
        if (featureWithChange.mainFeature.type === "delete") {
            continue;
        }

        const changedFieldsDefault: string[] = [];
        const changedFieldsOperative: string[] = [];
        for (const key in featureWithChange.mainFeature.defaultGraphic?.attributes) {
            if (
                featureWithChange.mainFeature.defaultGraphic?.attributes[key] !==
                featureWithChange.mainFeature.versionGraphic?.attributes[key]
            ) {
                changedFieldsDefault.push(key);
            }
        }
        featureWithChange.mainFeature.changedFieldsDefault = changedFieldsDefault;

        for (const key in featureWithChange.mainFeature.operativeGraphic?.attributes) {
            if (
                featureWithChange.mainFeature.operativeGraphic?.attributes[key] !==
                featureWithChange.mainFeature.versionGraphic?.attributes[key]
            ) {
                changedFieldsOperative.push(key);
            }
        }
        featureWithChange.mainFeature.changedFieldsOperative = changedFieldsOperative;

        // no default or operative graphic assume it is inserted
        // New objects that are not pushed to default yet
        if (!featureWithChange.mainFeature.operativeGraphic && !featureWithChange.mainFeature.defaultGraphic) {
            featureWithChange.mainFeature.changedFieldsDefault = Object.keys(
                featureWithChange.mainFeature.versionGraphic?.attributes || {}
            );
            // NB: do NOT `continue` here. The related features below still need their
            // changed fields computed — e.g. a newly created combined object where the
            // light (layer 0) is the new main feature and the IB (layer 1) is a related
            // sub-main feature. A `continue` would leave the IB with no changed fields.
        }
        // Has default graphic but no operative graphic, assume all operative fields changed
        // New objects that are not put into production yet
        else if (!featureWithChange.mainFeature.operativeGraphic && featureWithChange.mainFeature.defaultGraphic) {
            featureWithChange.mainFeature.changedFieldsOperative = Object.keys(
                featureWithChange.mainFeature.versionGraphic?.attributes || {}
            );
        }

        for (const relatedFeature of featureWithChange.relatedFeatures) {
            if (relatedFeature.type === "delete") {
                continue;
            }
            const relatedChangedFieldsDefault: string[] = [];
            const relatedChangedFieldsOperative: string[] = [];
            for (const key in relatedFeature.defaultGraphic?.attributes) {
                if (relatedFeature.defaultGraphic?.attributes[key] !== relatedFeature.versionGraphic?.attributes[key]) {
                    relatedChangedFieldsDefault.push(key);
                }
            }
            relatedFeature.changedFieldsDefault = relatedChangedFieldsDefault;

            for (const key in relatedFeature.operativeGraphic?.attributes) {
                if (
                    relatedFeature.operativeGraphic?.attributes[key] !== relatedFeature.versionGraphic?.attributes[key]
                ) {
                    relatedChangedFieldsOperative.push(key);
                }
            }
            relatedFeature.changedFieldsOperative = relatedChangedFieldsOperative;

            // no default or operative graphic assume it is inserted
            // New objects that are not pushed to default yet
            if (!relatedFeature.operativeGraphic && !relatedFeature.defaultGraphic) {
                relatedFeature.changedFieldsDefault = Object.keys(relatedFeature.versionGraphic?.attributes || {});
            }

            // Has default graphic but no operative graphic, assume all operative fields changed
            // New objects that are not put into production yet
            if (!relatedFeature.operativeGraphic && relatedFeature.defaultGraphic) {
                relatedFeature.changedFieldsOperative = Object.keys(relatedFeature.versionGraphic?.attributes || {});
            }
        }
    }

    return clonedFeaturesWithChanges;
};

const findAllRelatedFeaturesForMainFeature = async (
    mainFeature: FeatureObject,
    originalFeaturesMap: Map<string, FeatureObject>,
    versionName: string,
    keepRelatedOutsideOfInitialFeatures: boolean,
    isFromVersionChanges: boolean
): Promise<FeatureObject[]> => {
    const allRelatedFeatures: FeatureObject[] = [];
    const visitedFeatures = new Set<string>();
    const maxDepth = 3; // Limit recursion depth to ensure we don't jump from light to light if part of multiple overetts and similar cases

    // Mark the main feature as visited
    const mainFeatureKey = `${mainFeature.layerId}:${mainFeature.objectId}`;
    visitedFeatures.add(mainFeatureKey);

    // Start recursive search from the main feature
    await findRelatedFeaturesRecursive(
        [mainFeature],
        allRelatedFeatures,
        visitedFeatures,
        0,
        maxDepth,
        originalFeaturesMap,
        versionName,
        keepRelatedOutsideOfInitialFeatures,
        isFromVersionChanges
    );

    return allRelatedFeatures;
};

const findRelatedFeaturesRecursive = async (
    currentFeatures: FeatureObject[],
    allRelatedFeatures: FeatureObject[],
    visitedFeatures: Set<string>,
    currentDepth: number,
    maxDepth: number,
    originalFeaturesMap: Map<string, FeatureObject>,
    versionName: string,
    keepRelatedOutsideOfInitialFeatures: boolean,
    isFromVersionChanges: boolean
): Promise<void> => {
    if (currentDepth >= maxDepth || currentFeatures.length === 0) {
        return;
    }

    const nextLevelFeatures: FeatureObject[] = [];

    // Group features by layer for efficient batch processing
    const featuresByLayer = new Map<number, FeatureObject[]>();
    for (const feature of currentFeatures) {
        if (!featuresByLayer.has(feature.layerId)) {
            featuresByLayer.set(feature.layerId, []);
        }
        featuresByLayer.get(feature.layerId)!.push(feature);
    }

    // Process each layer
    for (const [, layerFeatures] of featuresByLayer) {
        const relatedFeaturesMap = await queryRelatedFeaturesForMultipleFeatures(
            layerFeatures,
            visitedFeatures,
            originalFeaturesMap,
            versionName,
            keepRelatedOutsideOfInitialFeatures
        ); // Collect all related features from this layer
        for (const relatedFeaturesList of relatedFeaturesMap.values()) {
            allRelatedFeatures.push(...relatedFeaturesList);
            nextLevelFeatures.push(...relatedFeaturesList);
        }
    }

    // Continue to next depth level
    await findRelatedFeaturesRecursive(
        nextLevelFeatures,
        allRelatedFeatures,
        visitedFeatures,
        currentDepth + 1,
        maxDepth,
        originalFeaturesMap,
        versionName,
        keepRelatedOutsideOfInitialFeatures,
        isFromVersionChanges
    );
};

const queryRelatedFeaturesForMultipleFeatures = async (
    features: FeatureObject[],
    visitedFeatures: Set<string>,
    originalFeaturesMap: Map<string, FeatureObject>,
    versionName: string,
    keepRelatedOutsideOfInitialFeatures: boolean
): Promise<Map<string, FeatureObject[]>> => {
    if (features.length === 0) {
        return new Map();
    }

    const resultMap = new Map<string, FeatureObject[]>();

    // Group features by layer since they might come from different layers
    const featuresByLayer = new Map<number, FeatureObject[]>();
    for (const feature of features) {
        if (!featuresByLayer.has(feature.layerId)) {
            featuresByLayer.set(feature.layerId, []);
        }
        featuresByLayer.get(feature.layerId)!.push(feature);
    }

    // Initialize result map for all features
    for (const feature of features) {
        const featureKey = `${feature.layerId}:${feature.objectId}`;
        resultMap.set(featureKey, []);
    }

    // Process each layer separately
    for (const [, layerFeatures] of featuresByLayer) {
        let featureLayer = layerFeatures[0].versionGraphic?.layer as FeatureLayer;
        featureLayer ??= layerFeatures[0].defaultGraphic?.layer as FeatureLayer;

        if (!featureLayer?.relationships?.length) {
            continue;
        }

        const objectIdField = featureLayer.objectIdField;
        const objectIds = layerFeatures.map((f) => f.versionGraphic!.attributes[objectIdField]);
        // console.log(objectIds);
        // console.log(layerFeatures)
        // Filter out relationships that point to layers 7, 8, or 13 to prevent chain reactions
        const blockedRelatedLayers = new Set([7, 8, 13]);
        const filteredRelationships = featureLayer.relationships.filter(
            (relationship) => !blockedRelatedLayers.has(relationship.relatedTableId)
        );

        // Batch query all relationships for features from this layer
        const relationshipQueries = filteredRelationships.map((relationship) =>
            queryRelationshipBatch(featureLayer, relationship, objectIds, versionName)
        );
        const relationshipResults = await Promise.all(relationshipQueries);

        // Process results and distribute to respective features
        for (const relationshipResult of relationshipResults) {
            for (const [objectId, relatedFeatureGroup] of relationshipResult.entries()) {
                // Find the source feature for this objectId
                const sourceFeature = layerFeatures.find(
                    (f) => f.versionGraphic!.attributes[objectIdField] === objectId
                );

                if (sourceFeature) {
                    const featureKey = `${sourceFeature.layerId}:${sourceFeature.objectId}`;
                    const newFeatures = processRelatedFeatures(
                        relatedFeatureGroup,
                        visitedFeatures,
                        originalFeaturesMap,
                        keepRelatedOutsideOfInitialFeatures
                    );
                    const existingFeatures = resultMap.get(featureKey) || [];
                    existingFeatures.push(...newFeatures);
                    resultMap.set(featureKey, existingFeatures);
                }
            }
        }
    }

    return resultMap;
};

const queryRelationshipBatch = async (
    featureLayer: FeatureLayer,
    relationship: any,
    objectIds: number[],
    versionName: string
): Promise<Map<number, Array<{ feature: any; relatedLayerId: number }>>> => {
    const resultMap = new Map<number, Array<{ feature: any; relatedLayerId: number }>>();

    const allLayers = getAllNFSLayers();
    if (!allLayers.length) {
        console.error("No layers found in the map.");
        return resultMap;
    }

    try {
        const relatedFeatureResults = await featureLayer.queryRelatedFeatures({
            relationshipId: relationship.id,
            objectIds: objectIds,
            outFields: ["*"],
            returnGeometry: true,
            geometryPrecision: 20,
            gdbVersion: versionName,
        });

        // Process results for each objectId
        for (const objectId of objectIds) {
            const relatedFeatureSet = relatedFeatureResults[objectId];
            if (relatedFeatureSet?.features) {
                const mappedFeatures = await Promise.all(
                    relatedFeatureSet.features.map(async (feature: Graphic) => {
                        let relatedFeatureLayer = allLayers.find(
                            (l) => (l as FeatureLayer).layerId === relationship.relatedTableId
                        ) as FeatureLayer;
                        if (!relatedFeatureLayer) {
                            relatedFeatureLayer = new FeatureLayer({
                                // This is a critical "hack" to make sure position of features are correct
                                portalItem: {
                                    id: AppConfig.NFSMapFeaturesPortalItemId,
                                    portal: {
                                        url: AppConfig.ArcGIS.BaseUrl,
                                    },
                                },
                                url: `${AppConfig.NauticalObjectsFeatureServer}${relationship.relatedTableId}`,
                                layerId: relationship.relatedTableId,
                                gdbVersion: versionName,
                            });
                            if (!relatedFeatureLayer.loaded) {
                                await relatedFeatureLayer.load();
                            }
                        }
                        feature.layer ??= relatedFeatureLayer;
                        return {
                            feature,
                            relatedLayerId: relationship.relatedTableId,
                        };
                    })
                );
                resultMap.set(objectId, mappedFeatures);
            } else {
                resultMap.set(objectId, []);
            }
        }
    } catch (error) {
        console.error(`Error querying relationship ${relationship.id}:`, error);
        // Initialize empty arrays for all objectIds on error
        for (const objectId of objectIds) {
            resultMap.set(objectId, []);
        }
    }

    return resultMap;
};

const processRelatedFeatures = (
    relatedFeatureGroup: Array<{ feature: any; relatedLayerId: number }>,
    visitedFeatures: Set<string>,
    originalFeaturesMap: Map<string, FeatureObject>,
    keepRelatedOutsideOfInitialFeatures: boolean
): FeatureObject[] => {
    const newFeatures: FeatureObject[] = [];

    for (const { feature, relatedLayerId } of relatedFeatureGroup) {
        const relatedObjectId =
            feature.attributes[feature.layer ? (feature.layer as FeatureLayer).objectIdField : "objectid"];
        const relatedFeatureKey = `${relatedLayerId}:${relatedObjectId}`;

        if (visitedFeatures.has(relatedFeatureKey)) {
            continue;
        }

        // Check if this related feature exists in the original features list
        const originalFeature = originalFeaturesMap.get(relatedFeatureKey);

        if (!originalFeature) {
            if (keepRelatedOutsideOfInitialFeatures) {
                visitedFeatures.add(relatedFeatureKey);

                // Create a new FeatureObject for this related feature
                const newFeatureObject: FeatureObject = {
                    type: "update", // Assuming it's an update; adjust as necessary
                    layerId: (feature.layer as FeatureLayer).layerId ?? relatedLayerId,
                    objectId: feature.attributes[feature.layer.objectIdField ?? "objectid"],
                    versionGraphic: feature,
                };
                newFeatures.push(newFeatureObject);
            }
            // Skip this feature as it's not part of the version differences
            continue;
        }

        visitedFeatures.add(relatedFeatureKey);

        // Use the original feature object instead of creating a new one
        // This ensures we have all the proper graphics (versionGraphic, defaultGraphic, operativeGraphic)
        newFeatures.push(originalFeature);
    }

    return newFeatures;
};

// Implement Function to go through FeatureWithChanges and fill out VersionGraphic, operativeGraphic and defaultGraphic if missing.
const findAndAddMissingGraphics = async (
    featuresWithChanges: FeatureWithChanges[],
    versionName: string
): Promise<FeatureWithChanges[]> => {
    const defaultLayerAndIdsToFetch: { [layerId: number]: { objectId: string; globalId: string }[] } = {};
    const operativeLayerAndIdsToFetch: { [layerId: number]: { objectId: string; globalId: string }[] } = {};
    const versionedLayerAndIdsToFetch: { [layerId: number]: { objectId: string; globalId: string }[] } = {};

    for (const featureWithChange of featuresWithChanges) {
        const { mainFeature } = featureWithChange;
        const mainFeatureAttributes =
            mainFeature.versionGraphic?.attributes ||
            mainFeature.defaultGraphic?.attributes ||
            mainFeature.operativeGraphic?.attributes;

        if (mainFeatureAttributes) {
            if (!mainFeature.versionGraphic) {
                if (!versionedLayerAndIdsToFetch[mainFeature.layerId]) {
                    versionedLayerAndIdsToFetch[mainFeature.layerId] = [];
                }
                versionedLayerAndIdsToFetch[mainFeature.layerId].push({
                    objectId: mainFeature.objectId,
                    globalId: mainFeatureAttributes?.globalid,
                });
            }
            if (!mainFeature.defaultGraphic) {
                if (!defaultLayerAndIdsToFetch[mainFeature.layerId]) {
                    defaultLayerAndIdsToFetch[mainFeature.layerId] = [];
                }
                defaultLayerAndIdsToFetch[mainFeature.layerId].push({
                    objectId: mainFeature.objectId,
                    globalId: mainFeatureAttributes?.globalid,
                });
            }
            if (!mainFeature.operativeGraphic) {
                if (!operativeLayerAndIdsToFetch[mainFeature.layerId]) {
                    operativeLayerAndIdsToFetch[mainFeature.layerId] = [];
                }
                operativeLayerAndIdsToFetch[mainFeature.layerId].push({
                    objectId: mainFeature.objectId,
                    globalId: mainFeatureAttributes?.globalid,
                });
            }
        }
        for (const relatedFeature of featureWithChange.relatedFeatures) {
            const relatedFeatureAttributes =
                relatedFeature.versionGraphic?.attributes ||
                relatedFeature.defaultGraphic?.attributes ||
                relatedFeature.operativeGraphic?.attributes;
            if (relatedFeatureAttributes) {
                if (!relatedFeature.versionGraphic) {
                    if (!versionedLayerAndIdsToFetch[relatedFeature.layerId]) {
                        versionedLayerAndIdsToFetch[relatedFeature.layerId] = [];
                    }
                    versionedLayerAndIdsToFetch[relatedFeature.layerId].push({
                        objectId: relatedFeature.objectId,
                        globalId: relatedFeatureAttributes?.globalid,
                    });
                }
                if (!relatedFeature.defaultGraphic) {
                    if (!defaultLayerAndIdsToFetch[relatedFeature.layerId]) {
                        defaultLayerAndIdsToFetch[relatedFeature.layerId] = [];
                    }
                    defaultLayerAndIdsToFetch[relatedFeature.layerId].push({
                        objectId: relatedFeature.objectId,
                        globalId: relatedFeatureAttributes?.globalid,
                    });
                }
                if (!relatedFeature.operativeGraphic) {
                    if (!operativeLayerAndIdsToFetch[relatedFeature.layerId]) {
                        operativeLayerAndIdsToFetch[relatedFeature.layerId] = [];
                    }
                    operativeLayerAndIdsToFetch[relatedFeature.layerId].push({
                        objectId: relatedFeature.objectId,
                        globalId: relatedFeatureAttributes?.globalid,
                    });
                }
            }
        }
    }

    // Fetch missing graphics in batches per layer
    const fetchPromises: Promise<void>[] = [];

    const allLayers = getAllNFSLayers();
    if (!allLayers.length) {
        console.error("No layers found in the map.");
        return featuresWithChanges;
    }

    const fetchGraphicsForLayer = async (
        layerId: number,
        idsToFetch: { objectId: string; globalId: string }[],
        type: "default" | "operative" | "versioned"
    ) => {
        let featureLayer = allLayers.find((l) => (l as FeatureLayer).layerId === layerId) as FeatureLayer;

        if (type === "operative") {
            if (!isOperativeLayerId(layerId)) {
                return;
            }

            featureLayer = new FeatureLayer({
                // This is a critical "hack" to make sure position of features are correct
                portalItem: {
                    id: AppConfig.OperativeObjectsPortalItemId,
                    portal: {
                        url: AppConfig.ArcGIS.BaseUrl,
                    },
                },
                url: `${AppConfig.OperativeObjectsFeatureServer}${layerId}`,
            });
        }

        if (!featureLayer) return;
        const objectIds = idsToFetch.map((i) => i.objectId);
        let gdbVersion = "sde.DEFAULT";
        if (type === "versioned") {
            gdbVersion = versionName;
        }
        try {
            let queriedFeatures;
            if (type === "operative") {
                queriedFeatures = await featureLayer.queryFeatures({
                    where: `globalid IN ('${idsToFetch.map((i) => i.globalId).join("','")}')`,
                    outFields: ["*"],
                    returnGeometry: true,
                    geometryPrecision: 20,
                });
            } else {
                queriedFeatures = await featureLayer.queryFeatures({
                    objectIds: objectIds,
                    outFields: ["*"],
                    returnGeometry: true,
                    geometryPrecision: 20,
                    gdbVersion: gdbVersion,
                });
            }
            for (const feature of queriedFeatures.features) {
                for (const featureWithChange of featuresWithChanges) {
                    if (
                        featureWithChange.mainFeature.layerId === layerId &&
                        featureWithChange.mainFeature.versionGraphic?.attributes.globalid ===
                            feature.attributes.globalid
                    ) {
                        if (type === "default" && !featureWithChange.mainFeature.defaultGraphic) {
                            feature.layer ??= featureLayer;
                            featureWithChange.mainFeature.defaultGraphic = feature;
                        } else if (type === "versioned" && !featureWithChange.mainFeature.versionGraphic) {
                            feature.layer ??= featureLayer;
                            featureWithChange.mainFeature.versionGraphic = feature;
                        } else if (type === "operative" && !featureWithChange.mainFeature.operativeGraphic) {
                            feature.layer ??= featureLayer;
                            featureWithChange.mainFeature.operativeGraphic = feature;
                        }
                    }
                    for (const relatedFeature of featureWithChange.relatedFeatures) {
                        if (
                            relatedFeature.layerId === layerId &&
                            relatedFeature.versionGraphic?.attributes.globalid === feature.attributes.globalid
                        ) {
                            if (type === "default" && !relatedFeature.defaultGraphic) {
                                feature.layer ??= featureLayer;
                                relatedFeature.defaultGraphic = feature;
                            } else if (type === "versioned" && !relatedFeature.versionGraphic) {
                                feature.layer ??= featureLayer;
                                relatedFeature.versionGraphic = feature;
                            } else if (type === "operative" && !relatedFeature.operativeGraphic) {
                                feature.layer ??= featureLayer;
                                relatedFeature.operativeGraphic = feature;
                            }
                        }
                    }
                }
            }
        } catch (error) {
            console.error(`Error fetching ${type} graphics for layer ${layerId}:`, error);
        }
    };
    for (const layerIdStr in defaultLayerAndIdsToFetch) {
        const layerId = Number.parseInt(layerIdStr);
        const idsToFetch = defaultLayerAndIdsToFetch[layerId];
        fetchPromises.push(fetchGraphicsForLayer(layerId, idsToFetch, "default"));
    }
    for (const layerIdStr in operativeLayerAndIdsToFetch) {
        const layerId = Number.parseInt(layerIdStr);
        const idsToFetch = operativeLayerAndIdsToFetch[layerId];
        fetchPromises.push(fetchGraphicsForLayer(layerId, idsToFetch, "operative"));
    }
    for (const layerIdStr in versionedLayerAndIdsToFetch) {
        const layerId = Number.parseInt(layerIdStr);
        const idsToFetch = versionedLayerAndIdsToFetch[layerId];
        fetchPromises.push(fetchGraphicsForLayer(layerId, idsToFetch, "versioned"));
    }
    await Promise.all(fetchPromises);

    return featuresWithChanges;
};

export const postHelperPointChangeToPlanRoot = async (
    feature: Graphic,
    versionGuid: string
): Promise<boolean> => {
    if (!feature || (feature.layer as FeatureLayer).layerId !== 35) {
        toast.error("Objektet du prøver å sende til planroot er ikke et hjelpepunkt.");
        return false;
    }

    const sessionID = getSessionID();
    const readStart = await startReading(versionGuid, sessionID);
    if (!readStart) {
        toast.error("Feil under oppstart av lesesesjon for sende hjelpepunktendring til planroot.");
        return false;
    }
    const editStart = await startEditing(versionGuid, sessionID);
    if (!editStart) {
        toast.error("Feil under oppstart av redigeringssesjon for sende hjelpepunktendring til planroot.");
        await stopReading(versionGuid, sessionID);
        return false;
    }
    await reconcileVersion(versionGuid, sessionID, false, "byAttribute", false);
    const differencesObjectIds = await getDifferencesObjectIds(versionGuid, sessionID, true);
    const hjelpepunktObjectId = feature.attributes[(feature.layer as FeatureLayer).objectIdField ?? "objectid"];
    let changeType = "";
    let hasHjelpepunktChange = false;
    for (const layerChanges of differencesObjectIds) {
        if (layerChanges.layerId === 35) {
            if (layerChanges.inserts?.includes(hjelpepunktObjectId)) {
                hasHjelpepunktChange = true;
                changeType = "opprettelse";
                break;
            }
            if (layerChanges.updates?.includes(hjelpepunktObjectId)) {
                hasHjelpepunktChange = true;
                changeType = "oppdatering";
                break;
            }
            if (layerChanges.deletes?.includes(hjelpepunktObjectId)) {
                hasHjelpepunktChange = true;
                changeType = "sletting";
                break;
            }
        }
    }

    if (!hasHjelpepunktChange || !hjelpepunktObjectId) {
        toast.error("Kunne ikke finne endring for hjelpepunktet i versjonen.");
        await stopEditing(versionGuid, sessionID, true);
        await stopReading(versionGuid, sessionID);
        return false;
    }
    toast.info(`Starter sending av hjelpepunkt ${changeType} til planroot...`);
    const postResult = await postChanges(versionGuid, sessionID, [{ layerId: 35, objectIds: [hjelpepunktObjectId] }]);
    if (postResult) {
        toast.success(`Hjelpepunkt ${changeType} sendt til planroot.`);
        await reconcileVersion(versionGuid, sessionID, false, "byAttribute", false);
    } else {
        toast.error("Feil under sending av hjelpepunktendring til planroot.");
    }
    await stopEditing(versionGuid, sessionID, true);
    await stopReading(versionGuid, sessionID);
    return postResult;
};

/**
 * POSTs one or more features' version-side changes to the DEFAULT version in a single
 * reconcile + post cycle. Returns true only if the post itself succeeded.
 *
 * Intended for the direct-to-default fast path. The primary use case is posting a
 * (primary, secondary) pair atomically — see runFastPath — so we group all objectIds into
 * a single `postChanges` call rather than running one cycle per feature. Caller must
 * ensure each feature actually has pending changes in the named version.
 */
export const postFeaturesToDefault = async (features: Graphic[], versionGuid: string): Promise<boolean> => {
    if (features.length === 0) {
        toast.error("Ingen objekter å poste til planroot.");
        return false;
    }

    // Build the rowsParameter, grouping objectIds by layerId so the version service
    // receives one entry per layer even if the same layer appears twice.
    const objectIdsByLayer = new Map<number, number[]>();
    for (const feature of features) {
        const layer = feature.layer as FeatureLayer;
        if (!layer || layer.layerId === undefined || layer.layerId === null) {
            toast.error("Mangler lag-informasjon på et objekt. Kunne ikke poste til planroot.");
            return false;
        }
        const objectId = feature.attributes[layer.objectIdField ?? "objectid"];
        if (objectId === undefined || objectId === null) {
            toast.error("Mangler objectid på et objekt. Kunne ikke poste til planroot.");
            return false;
        }
        const bucket = objectIdsByLayer.get(layer.layerId) ?? [];
        bucket.push(Number(objectId));
        objectIdsByLayer.set(layer.layerId, bucket);
    }
    const rowsParameter = Array.from(objectIdsByLayer.entries()).map(([layerId, objectIds]) => ({
        layerId,
        objectIds,
    }));

    const sessionID = getSessionID();
    const readStart = await startReading(versionGuid, sessionID);
    if (!readStart) {
        toast.error("Feil under oppstart av lesesesjon for direkte sending til planroot.");
        return false;
    }
    const editStart = await startEditing(versionGuid, sessionID);
    if (!editStart) {
        toast.error("Feil under oppstart av redigeringssesjon for direkte sending til planroot.");
        await stopReading(versionGuid, sessionID);
        return false;
    }
    if (!(await reconcileOrAbortOnConflicts(versionGuid, sessionID))) {
        await stopEditing(versionGuid, sessionID, false);
        await stopReading(versionGuid, sessionID);
        return false;
    }

    // The post endpoint rejects objectIds that have no actual differences in the version.
    // When a pair is posted but only one side was edited, sending the untouched side's
    // objectId causes the whole call to fail — so filter the rowsParameter against the
    // version's real differences first.
    const differences = await getDifferencesObjectIds(versionGuid, sessionID, true);
    const changedKeysByLayer = new Map<number, Set<string>>();
    for (const diff of differences) {
        const layerSet = changedKeysByLayer.get(diff.layerId) ?? new Set<string>();
        for (const oid of diff.inserts ?? []) layerSet.add(String(oid));
        for (const oid of diff.updates ?? []) layerSet.add(String(oid));
        for (const oid of diff.deletes ?? []) layerSet.add(String(oid));
        changedKeysByLayer.set(diff.layerId, layerSet);
    }
    const filteredRowsParameter = rowsParameter
        .map(({ layerId, objectIds }) => ({
            layerId,
            objectIds: objectIds.filter((oid) => changedKeysByLayer.get(layerId)?.has(String(oid))),
        }))
        .filter((row) => row.objectIds.length > 0);
    if (filteredRowsParameter.length === 0) {
        toast.error("Ingen av objektene har endringer i versjonen — kunne ikke poste til planroot.");
        await stopEditing(versionGuid, sessionID, false);
        await stopReading(versionGuid, sessionID);
        return false;
    }

    const postResult = await postChanges(versionGuid, sessionID, filteredRowsParameter);
    if (postResult) {
        await reconcileVersion(versionGuid, sessionID, true, "byAttribute", false);
    }

    await stopEditing(versionGuid, sessionID, true);
    await stopReading(versionGuid, sessionID);
    return postResult;
};

/**
 * Back-compat wrapper for the single-feature post path. New callers should prefer
 * `postFeaturesToDefault` which accepts a pair.
 */
export const postSingleFeatureToDefault = async (feature: Graphic, versionGuid: string): Promise<boolean> =>
    postFeaturesToDefault([feature], versionGuid);

export const deleteHelperpoint = async (feature: Graphic, versionGuid: string): Promise<boolean> => {
    if (!feature || (feature.layer as FeatureLayer).layerId !== 35) {
        toast.error("Objektet du prøver å slette ikke er et hjelpepunkt.");
        return false;
    }

    if (feature.layer?.type !== "feature") {
        toast.error("Kunne ikke slette hjelpepunktet. Ugyldig lagtype.");
        return false;
    }

    await applyEdits(feature.layer as FeatureLayer, { deleteFeatures: [feature] });
    const existsOnDefault = await checkIfObjectExistsOnDefault(feature);
    if (existsOnDefault) {
        toast.info("Sender sletting av hjelpepunkt til planroot...");
        await postHelperPointChangeToPlanRoot(feature, versionGuid);
    } else {
        toast.success("Hjelpepunkt slettet.");
    }
    return true;
};

export const getVersionInfoJson = async (versionGuid: string): Promise<VersionInfoExtended | null> => {
    let versionInfo: VersionInfoExtended | null = null;
    const versionServiceUrl = getVersionServiceUrl();
    const token = await getAGEToken();
    await fetch(`${versionServiceUrl}/versions/${versionGuid}?f=json`, {
        method: "POST",
        headers: {
            "X-Esri-Authorization": "Bearer " + token,
        },
    })
        .then(async (response) => {
            await response.json().then((data) => {
                if (data.success) {
                    versionInfo = data;
                } else {
                    console.error("Failed to get version info:", data.error);
                }
            });
        })
        .catch((error) => {
            console.error(error);
        });
    return versionInfo;
};

const getAllNFSLayers = (): FeatureLayer[] => {
    const mapView = getMapView();
    if (!mapView?.map) {
        return [];
    }

    let allLayers = mapView.map.allLayers.toArray();
    allLayers = allLayers.concat(mapView.map.allTables.toArray());

    const nfsFeaturesLayerUrlNoTrailingSlash = AppConfig.NauticalObjectsFeatureServer.replace(/\/$/, "");
    return allLayers.filter(
        (l) =>
            l.type === "feature" && (l as FeatureLayer).url?.replace(/\/$/, "") === nfsFeaturesLayerUrlNoTrailingSlash
    ) as FeatureLayer[];
};
